import { Badge, Button, type BadgeTone } from './ui.js';
import type { SpecContent, SuggestionKind } from '../api/types.js';
import { downloadTextFile, fileStem, specToMarkdown } from '../spec-markdown.js';

const SUGGESTION_TONES: Record<SuggestionKind, BadgeTone> = {
  feature: 'success',
  change: 'progress',
  risk: 'danger',
};

/** A prominent section heading, shared across the spec's sections. */
function SectionHeading({ children }: { children: string }) {
  return (
    <h3 className="mb-2 border-b border-secondary/25 pb-1.5 font-title text-xl font-semibold tracking-wide text-secondary">
      {children}
    </h3>
  );
}

function Prose({ heading, body }: { heading: string; body: string }) {
  return (
    <div>
      <SectionHeading>{heading}</SectionHeading>
      <p className="whitespace-pre-wrap leading-relaxed">{body}</p>
    </div>
  );
}

function List({ heading, items }: { heading: string; items: string[] }) {
  return (
    <div>
      <SectionHeading>{heading}</SectionHeading>
      <ul className="list-disc space-y-1 pl-5 leading-relaxed">
        {items.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    </div>
  );
}

/** Read-only render of a generated specification's content. */
export function SpecView({
  content,
  downloadName,
}: {
  content: SpecContent;
  downloadName?: string;
}) {
  return (
    <div className="flex flex-col gap-5">
      {downloadName ? (
        <div className="flex justify-end">
          <Button
            variant="secondary"
            onClick={() =>
              downloadTextFile(
                `${fileStem(downloadName)}.md`,
                specToMarkdown(content, downloadName),
              )
            }
          >
            Download .md
          </Button>
        </div>
      ) : null}
      <Prose heading="Context" body={content.context} />
      <Prose heading="Business problem" body={content.business_problem} />
      <Prose heading="Target users" body={content.target_users} />
      <List heading="Functional requirements" items={content.functional_requirements} />
      <List heading="Non-functional requirements" items={content.non_functional_requirements} />
      <Prose heading="Time to market" body={content.time_to_market} />
      <Prose heading="Branding" body={content.branding} />

      {content.suggestions.length > 0 ? (
        <div>
          <SectionHeading>Suggestions from Foundry AI</SectionHeading>
          <ul className="flex flex-col gap-2">
            {content.suggestions.map((s, i) => (
              <li key={i} className="rounded-md bg-neutral/60 p-3">
                <div className="mb-1 flex items-center gap-2">
                  <Badge tone={SUGGESTION_TONES[s.kind]}>{s.kind}</Badge>
                  <span className="font-medium">{s.title}</span>
                </div>
                <p className="text-sm opacity-80">{s.rationale}</p>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
